import React, { FC, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, List, ListItem, ListSubheader, Paper } from '@mui/material';
import { SimpleSubCategory } from 'models/order-input/simple-order';
import { SimpleOrderContext } from 'pages/order-flow/simple-order/simple-order-context';
import { SupplierOrderContext } from 'pages/order-flow/supplier-order/supplier-order-context';

type CurrentJobs = {
    name: string;
    deliveryDays: number;
    active: boolean;
};

type SelectConstructionCategoryProps = { onCateogrySelected: (category: SimpleSubCategory) => void };
const SelectConstructionCategory: FC<SelectConstructionCategoryProps> = ({ onCateogrySelected }) => {
    const { t: translate } = useTranslation(['orders']);
    const { order } = useContext(SimpleOrderContext);
    const { order: supplierOrder } = useContext(SupplierOrderContext);

    const getActiveConstructions = (constructions: SimpleSubCategory[], lab?: { currentJobs?: CurrentJobs[] }) => {
        // Even if no jobs, keep OTHER and OTHER_REMOVABLE
        if (!lab?.currentJobs?.length) {
            return constructions.filter(
                (c) => c === SimpleSubCategory.OTHER || c === SimpleSubCategory.OTHER_REMOVABLE,
            );
        }

        return constructions.filter((construction) => {
            const value = construction.toLowerCase();

            // Always keep OTHER and OTHER_REMOVABLE
            if (value === 'other' || value === 'other_removable') return true;

            // Match with current jobs (case-insensitive)
            const job = lab.currentJobs?.find((j) => j.name.toLowerCase() === value && j.active);

            return !!job;
        });
    };

    const constructionList = (title: string, items: SimpleSubCategory[]) => {
        const activeConstructions = getActiveConstructions(items, order.supplier || supplierOrder.supplier);

        return (
            <Paper variant="outlined">
                <List
                    sx={{ width: 400 }}
                    subheader={<ListSubheader sx={{ fontSize: '1.3em', textAlign: 'center' }}>{title}</ListSubheader>}
                >
                    {activeConstructions.map((construction) => (
                        <ListItem key={construction}>
                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                disableElevation
                                onClick={() => onCateogrySelected(construction)}
                            >
                                {translate(`orders:${construction}`)}
                            </Button>
                        </ListItem>
                    ))}
                </List>
            </Paper>
        );
    };

    return (
        <Box height="500px" display="flex" justifyContent="center" alignItems="center">
            <Box display="flex" justifyContent="center">
                <Box display="flex" justifyContent="center" gap={4}>
                    {constructionList(translate('orders:FIXED'), fixedConstructions)}
                    {constructionList(translate('orders:REMOVABLE'), removableConstructions)}
                </Box>
            </Box>
        </Box>
    );
};

const fixedConstructions = [
    SimpleSubCategory.SINGLE_CROWN,
    SimpleSubCategory.BRIDGE,
    SimpleSubCategory.POST,
    SimpleSubCategory.IMPLANT_CROWN,
    SimpleSubCategory.IMPLANT_BRIDGE,
    SimpleSubCategory.OTHER,
];
const removableConstructions = [
    SimpleSubCategory.COMPLETE_DENTURE,
    SimpleSubCategory.PARTIAL_DENTURE,
    SimpleSubCategory.TEMPORARY_DENTURE,
    SimpleSubCategory.BITETRAY,
    SimpleSubCategory.OTHER_REMOVABLE,
];

export default SelectConstructionCategory;
